/**
 * The smaller client tabs: Reports (slide decks and documents), Client
 * Portal (what the client sees, and their link), and Settings (the client's
 * details, and deleting the client).
 */

import { useEffect, useState, type FormEvent, type ReactNode } from 'react';

import type { Branding } from '../portal/portal-api.ts';
import { agencyApi } from '../reports/agency-api.ts';
import { DocumentItem } from '../reports/DocumentItem.tsx';
import { CoverPage } from '../reports/PageArt.tsx';
import { RemovedDocuments } from '../reports/RemovedDocuments.tsx';
import { reportsApi, type Client, type ReportDocument } from '../reports/reports-api.ts';
import { WHITE_LABEL_ON } from '../whitelabel/switch.ts';
import { portalUrl, whiteLabelApi } from '../whitelabel/whitelabel-api.ts';
import { clientsApi } from './clients-api.ts';
import { go } from './routes.ts';

export function ClientReportsTab(props: {
  organisationId: string;
  clientId: string;
  documents: ReportDocument[];
  onCreate: () => void;
  onOpen: (id: string) => void;
  onChanged: () => void;
}): ReactNode {
  const reports = props.documents.filter((document) => document.kind !== 'DASHBOARD');
  return (
    <div className="ag-tab-body">
      <header className="ag-tab-head">
        <h2>Reports</h2>
        <span className="ag-spacer" />
        <button type="button" className="btn" onClick={props.onCreate}>
          Create Report
        </button>
      </header>
      {reports.length === 0 ? (
        <div className="ag-feature">
          <h2>Create your first report</h2>
          <p className="ag-feature-lead">
            A slide deck for presenting, or a document for reading — cover, contents, results and
            your notes.
          </p>
          <ul>
            <li>Start from a template, blank, or a copy</li>
            <li>Present it full screen in a meeting</li>
            <li>Share it with your client in their portal</li>
          </ul>
          <button type="button" className="btn" onClick={props.onCreate}>
            Create Report
          </button>
        </div>
      ) : (
        <ul className="rh-list">
          {reports.map((document) => (
            <DocumentItem
              key={document.id}
              document={document}
              onOpen={() => props.onOpen(document.id)}
              onDelete={async () => {
                await reportsApi.remove(props.organisationId, document.id);
                props.onChanged();
              }}
            />
          ))}
        </ul>
      )}
      <RemovedDocuments
        organisationId={props.organisationId}
        clientId={props.clientId}
        show="reports"
        refreshKey={props.documents}
        onRestored={props.onChanged}
      />
    </div>
  );
}

export function ClientPortalTab(props: {
  organisationId: string;
  client: Client;
  documents: ReportDocument[];
  branding: Branding;
  onChanged: () => void;
}): ReactNode {
  const [problem, setProblem] = useState<string | null>(null);
  // C7 — the agency's portal address (custom domain, else subdomain); this site until one is set.
  const [portalLink, setPortalLink] = useState(`${window.location.origin}/`);
  useEffect(() => {
    if (!WHITE_LABEL_ON) return;
    whiteLabelApi
      .get(props.organisationId)
      .then((state) => {
        const url = portalUrl(state);
        if (url) setPortalLink(`${url}/`);
      })
      .catch(() => undefined);
  }, [props.organisationId]);
  return (
    <div className="ag-tab-body ag-portal-grid">
      <section className="card">
        <h2>What {props.client.name} sees</h2>
        <p className="panel-note">Tick a dashboard or report to show it in the client's portal.</p>
        {problem && (
          <p className="rh-access-msg rh-access-error" role="alert">
            {problem}
          </p>
        )}
        <ul className="ag-share-list">
          {props.documents.map((document) => (
            <li key={document.id}>
              <label>
                <input
                  type="checkbox"
                  checked={Boolean(document.shared_at)}
                  onChange={(event) =>
                    void agencyApi.share(
                      props.organisationId,
                      document.id,
                      event.target.checked,
                      props.onChanged,
                      setProblem,
                    )
                  }
                />
                {document.name}
              </label>
            </li>
          ))}
          {props.documents.length === 0 && (
            <li className="rh-source-none">Nothing to share yet.</li>
          )}
        </ul>
        <h3>Portal link</h3>
        <p className="panel-note">
          Client users sign in here with the invitation you send from the Users tab.
        </p>
        <code className="ag-link">{portalLink}</code>
        <button
          type="button"
          className="btn small secondary"
          onClick={() =>
            go({ area: 'client', clientId: props.client.id, tab: 'users', documentId: null })
          }
        >
          Manage client users
        </button>
      </section>
      <section className="card">
        <h2>Portal preview</h2>
        <p className="panel-note">Your agency's branding. Change it under Settings → Branding.</p>
        <div className="rbd-preview">
          <CoverPage
            facts={{
              reportName: 'Monthly marketing report',
              clientName: props.client.name,
              periodLabel: 'Last month',
              preparedBy: props.branding.display_name,
              logo: props.branding.logo,
              accent: props.branding.accent,
            }}
          />
        </div>
      </section>
    </div>
  );
}

export function ClientSettingsTab(props: {
  organisationId: string;
  client: Client;
  onChanged: () => void;
}): ReactNode {
  const [name, setName] = useState(props.client.name);
  const [website, setWebsite] = useState(props.client.website ?? '');
  const [timezone, setTimezone] = useState(props.client.timezone ?? 'Africa/Johannesburg');
  const [message, setMessage] = useState<{ tone: 'ok' | 'error'; text: string } | null>(null);
  const [confirming, setConfirming] = useState(false);
  const save = async (event: FormEvent) => {
    event.preventDefault();
    try {
      await clientsApi.update(props.organisationId, props.client.id, {
        name: name.trim(),
        website: website.trim() || null,
        timezone,
      });
      setMessage({ tone: 'ok', text: 'Saved.' });
      props.onChanged();
    } catch (error) {
      setMessage({ tone: 'error', text: error instanceof Error ? error.message : 'Not saved.' });
    }
  };
  return (
    <div className="ag-tab-body">
      <form className="card ag-settings" onSubmit={(event) => void save(event)}>
        <h2>Client details</h2>
        {message && (
          <p className={`rh-access-msg rh-access-${message.tone}`} role="status">
            {message.text}
          </p>
        )}
        <label className="rs-field">
          Client name
          <input value={name} onChange={(event) => setName(event.target.value)} required />
        </label>
        <label className="rs-field">
          Website
          <input
            value={website}
            onChange={(event) => setWebsite(event.target.value)}
            placeholder="example.co.za"
          />
        </label>
        <label className="rs-field">
          Time zone
          <input value={timezone} onChange={(event) => setTimezone(event.target.value)} />
        </label>
        <div className="rc-actions">
          <button type="submit" className="btn">
            Save
          </button>
        </div>
      </form>
      <section className="card ag-danger">
        <h2>Delete client</h2>
        <p className="panel-note">
          Its dashboards, reports and portal access stop being available. History is kept.
        </p>
        {confirming ? (
          <span className="rh-access-confirm">
            <button
              type="button"
              className="btn danger"
              onClick={() =>
                void clientsApi.remove(props.organisationId, props.client.id).then(() => {
                  props.onChanged();
                  go({ area: 'clients' });
                })
              }
            >
              Delete {props.client.name}
            </button>
            <button type="button" className="btn secondary" onClick={() => setConfirming(false)}>
              Keep
            </button>
          </span>
        ) : (
          <button type="button" className="btn secondary" onClick={() => setConfirming(true)}>
            Delete client
          </button>
        )}
      </section>
    </div>
  );
}
